// @ts-nocheck
import { Button } from '@/components/ui/core';
import { Stack, Group } from '@/components/ui/layout';
import { Divider } from '@/components/ui/data-display';
import { Text } from '@/components/ui/core';
import { IconBrandGoogle, IconBrandApple, IconBrandGithub, IconBrandFacebook } from '@tabler/icons-react';

export type SocialProvider = 'google' | 'apple' | 'github' | 'facebook';
export type ButtonLayout = 'horizontal' | 'vertical' | 'icons-only';
export type ButtonVariant = 'default' | 'gradient' | 'outline';

interface SocialLoginButtonsProps {
  providers?: SocialProvider[];
  layout?: ButtonLayout;
  variant?: ButtonVariant;
  showDivider?: boolean;
  dividerLabel?: string;
  onProviderClick?: (provider: SocialProvider) => void;
}

const providerConfig = {
  google: {
    label: 'Google',
    icon: IconBrandGoogle,
    color: '#DB4437',
  },
  apple: {
    label: 'Apple',
    icon: IconBrandApple,
    color: '#000000',
  },
  github: {
    label: 'GitHub',
    icon: IconBrandGithub,
    color: '#333333',
  },
  facebook: {
    label: 'Facebook',
    icon: IconBrandFacebook,
    color: '#1877F2',
  },
};

export default function SocialLoginButtons({
  providers = ['google', 'apple', 'github'],
  layout = 'vertical',
  variant = 'default',
  showDivider = true,
  dividerLabel = 'Or continue with',
  onProviderClick,
}: SocialLoginButtonsProps) {
  const handleClick = (provider: SocialProvider) => {
    onProviderClick?.(provider);
  };

  const getButtonProps = (provider: SocialProvider) => {
    const config = providerConfig[provider];
    const Icon = config.icon;

    const baseProps = {
      leftSection: layout !== 'icons-only' ? <Icon size={18} /> : undefined,
      onClick: () => handleClick(provider),
      fullWidth: layout !== 'icons-only',
    };

    switch (variant) {
      case 'gradient':
        return {
          ...baseProps,
          variant: 'gradient' as const,
          gradientVariant: 'brand' as const,
        };
      case 'outline':
        return {
          ...baseProps,
          variant: 'outline' as const,
        };
      default:
        return {
          ...baseProps,
          variant: 'default' as const,
        };
    }
  };

  const renderButtons = () => {
    if (layout === 'icons-only') {
      return (
        <Group justify="center" gap="md">
          {providers.map((provider) => {
            const config = providerConfig[provider];
            const Icon = config.icon;
            return (
              <Button
                key={provider}
                variant={variant === 'outline' ? 'outline' : 'default'}
                size="lg"
                onClick={() => handleClick(provider)}
                style={{ width: 48, padding: 0 }}
              >
                <Icon size={20} />
              </Button>
            );
          })}
        </Group>
      );
    }

    if (layout === 'horizontal') {
      return (
        <Group grow gap="sm">
          {providers.map((provider) => {
            const config = providerConfig[provider];
            return (
              <Button key={provider} {...getButtonProps(provider)} size="sm">
                {config.label}
              </Button>
            );
          })}
        </Group>
      );
    }

    return (
      <Stack gap="sm">
        {providers.map((provider) => {
          const config = providerConfig[provider];
          return (
            <Button key={provider} {...getButtonProps(provider)}>
              Continue with {config.label}
            </Button>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack gap="md">
      {showDivider && (
        <Divider
          label={
            <Text size="xs" c="dimmed">
              {dividerLabel}
            </Text>
          }
          labelPosition="center"
        />
      )}
      {renderButtons()}
    </Stack>
  );
}
